import { useNavigate } from "react-router";
import { useTenant } from "../../Contexts/TenantContext";
import { ShippingMethod } from "../../Enums/ShippingMethod";
import { toPublicPath } from "../../Utils/publicRoutes";
import { FaCheckCircle, FaWhatsapp } from "react-icons/fa";
import { toast } from "react-toastify";
import Button from "../Button/Button";

type OrderCreatedStepProps = {
  orderNumber: number;
  hasMadeToOrderItems: boolean;
  shippingMethod?: ShippingMethod | null;
};

const OrderCreatedStep = ({
  orderNumber,
  hasMadeToOrderItems,
  shippingMethod
}: OrderCreatedStepProps) => {
  const navigate = useNavigate();
  const { tenantContact } = useTenant();

  const getPaymentMessages = (): string[] => {
    const notes: string[] = [];

    if (hasMadeToOrderItems && shippingMethod === ShippingMethod.CustomCarrier) {
      notes.push("Seu pedido contém itens sob encomenda e o envio será por transportadora a combinar.");
      notes.push("Aguarde as informações de disponibilidade e entrega antes de prosseguir para o pagamento.");
    } else {
      if (hasMadeToOrderItems) {
        notes.push("Seu pedido contém itens sob encomenda.");
        notes.push("Aguarde as informações de disponibilidade antes de prosseguir para o pagamento.");
      }

      if (shippingMethod === ShippingMethod.CustomCarrier) {
        notes.push("Envio via transportadora a combinar.");
        notes.push("Aguarde as informações de entrega antes de prosseguir para o pagamento.");
      }
    }

    if (notes.length === 0) {
      notes.push("Agora é só aguardar as informações de entrega antes de prosseguir para o pagamento.");
    }

    return notes;
  };

  const handleNavigateToMyOrders = () => {
    navigate(toPublicPath("/pedidos"));
  };

  const handleNavigateToHome = () => {
    navigate("/");
  };

  const handleContactCustomerSupport = () => {
    if (!tenantContact?.whatsAppNumber || !orderNumber) {
      toast.error("Informações de contato ou número do pedido não estão disponíveis.");
      return;
    }

    const message = `Olá, fiz um pedido de n° ${orderNumber}. Estou aguardando informações sobre a entrega, poderia me atualizar sobre o mesmo?`;
    const encodedMessage = encodeURIComponent(message);
    const url = `https://wa.me/${tenantContact.whatsAppNumber}?text=${encodedMessage}`;

    window.open(url, "_blank");
  };

  return (
    <div className="flex flex-col items-center text-center w-full p-4 md:p-6 gap-3">
      <FaCheckCircle className="text-primary-600 text-3xl md:text-4xl" />
      <h2 className="text-xl font-bold text-primary-600">{"Pedido criado com sucesso!"}</h2>
      <p className="text-sm md:text-base">
        {getPaymentMessages().map((message, index) => (
          <span key={index} className="block">
            {message}
          </span>
        ))}
      </p>
      <div className="w-full flex flex-col md:flex-row gap-2 md:gap-4 mt-2">
        <Button
          className="w-full py-2"
          onClick={handleNavigateToMyOrders}
        >
          {"Ir para meus pedidos"}
        </Button>
        <Button variant="secondary"
          className="w-full py-2"
          onClick={handleNavigateToHome}
        >
          {"Voltar para a tela inicial"}
        </Button>
        <Button variant="whatsapp"
          className="flex justify-center items-center gap-2 w-full py-2"
          onClick={handleContactCustomerSupport}
        >
          <FaWhatsapp />
          {"Falar com o atendimento"}
        </Button>
      </div>
    </div>
  );
};

export default OrderCreatedStep;
